import { Alert, App, Flex } from "antd";
import { BulkRequestPreview } from "./BulkRequestPreview/BulkRequestPreview";
import { RequestChannel } from "@vyne-shared/api/VynePayments/responses";
import { VyneDescriptions } from "@vyne-shared/components/vyneDescriptions/VyneDescriptions";
import { VyneModal } from "@vyne-shared/components/vyneModal/VyneModal";
import { antdModalV5, create, useModal } from "@ebay/nice-modal-react";
import { displaySubmissionError } from "./displayBulkRequestSubmissionError";
import type { BulkRequestRecord } from "api/clients/useBulkRequestQuery";

interface BulkRequestSubmitConfirmModalContentProps {
    readonly submitData: BulkRequestRecord[];
    readonly invalidCount: number;
}

const BulkRequestSubmitConfirmModalContent = ({
    submitData,
    invalidCount,
}: BulkRequestSubmitConfirmModalContentProps) => {
    const emailEntries = submitData.some(
        (record) => record.requestChannel === RequestChannel.Email || record.requestChannel === RequestChannel.Both,
    );

    const phoneEntries = submitData.some(
        (record) => record.requestChannel === RequestChannel.SMS || record.requestChannel === RequestChannel.Both,
    );

    let requestMethodText = "SMS & Email";
    if (emailEntries && !phoneEntries) {
        requestMethodText = "Email";
    } else if (!emailEntries && phoneEntries) {
        requestMethodText = "SMS";
    }

    return (
        <Flex vertical={true} gap={16}>
            <VyneDescriptions
                column={1}
                layout="horizontal"
                items={[
                    {
                        label: "Responsible Parties",
                        children: submitData.length,
                    },
                    {
                        label: "Request Method",
                        children: requestMethodText,
                    },
                ]}
            />

            {invalidCount > 0 && (
                <Alert
                    message={`${invalidCount} ${invalidCount == 1 ? "Responsible Party does" : "Responsible Parties do"} not have valid contact information and will not be able to be messaged.`}
                    type="warning"
                    showIcon
                />
            )}

            <BulkRequestPreview />
        </Flex>
    );
};

interface BulkRequestSubmitConfirmModalProps extends BulkRequestSubmitConfirmModalContentProps {
    readonly setConfirming: (x: boolean) => void;
    readonly submit: () => Promise<void>;
}

export const BulkRequestSubmitConfirmModal = create<BulkRequestSubmitConfirmModalProps>(
    ({ setConfirming, submit, submitData, invalidCount }) => {
        const { modal: ANTDModal } = App.useApp();

        const modal = useModal();
        const { afterClose, onOk, ...antdModalProps } = antdModalV5(modal);

        return (
            <VyneModal
                {...antdModalProps}
                className="vyne-bulk-request-confirm-modal"
                title="Batch Payment Request"
                okText="Send"
                onOk={() => {
                    void submit().then(onOk, (reason: unknown) => {
                        const computedReason =
                            reason instanceof Error
                                ? reason
                                : new Error("Bulk Request submission failed", { cause: reason });

                        displaySubmissionError(ANTDModal, computedReason);
                    });
                }}
                okButtonProps={{ id: "bulk-request-confirm-send-btn" }}
                afterClose={() => {
                    setConfirming(false);
                    afterClose();
                }}
                cancelText="Cancel"
                cancelButtonProps={{ id: "bulk-request-cancel-btn" }}
                width={758}
            >
                <BulkRequestSubmitConfirmModalContent submitData={submitData} invalidCount={invalidCount} />
            </VyneModal>
        );
    },
);
